import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import LayoutContent from "../../LayoutContent";
import Header from "../../Header/Header";
import Breadcrumb from "../../Header/Breadcrumb";
import HeaderButton from "../../Header/HeaderButton";
import SwitchButton from "../../Buttons/SwitchButton";
import MtiButton from "../../Buttons/MtiButton";
import { getModules } from "../../../redux/reducers/ModuleReducer";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { EModuleType } from "../../../Model/EModuleType";
import { getModuleCapability } from "../../../redux/reducers/ModuleCapabilitiesReducer";
import SettingsNavSubMenu from "../SettingsNavSubMenu";
import {
  ECarrierAutoCalibrationType,
  EBarcodeScannerType,
  PsrCameraCapabilities,
} from "../../../Model/PsrCapabilites";
import PsrCaptureProfileApi from "../../../api/PsrCaptureProfileApi";
import { NamedPsrCaptureProfile } from "../../../Model/CaptureProfile";
import RaspiCameraSettings from "./RaspiCameraSettings";
import HeaderDropDownButton from "../../Header/HeaderDropDownButton";
import IconMoreAction from "../../../Icons/TopCommandPanel/MoreAction";
import IconExport from "../../../Icons/TopCommandPanel/Export";
import IconImport from "../../../Icons/TopCommandPanel/Import";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../../redux/actions/NotificationAction";
import { ENotificationType } from "../../../Model/ENotificationType";
import Modal from "../../Common/Modal";
import { getModuleState } from "../../../redux/reducers/ModuleStateReducer";
import PsrCameraApi from "../../../api/PsrCameraApi";
import UserConfirmationModalView from "../../Common/ModalView/UserConfirmationModalView";
import { useConfirm } from "../../Hooks/WindowHooks";
import PsrCameraLiveImage from "../Control/PsrCameraLiveImage";

export default function SettingsRaspiCamera() {
  const dispatch = useDispatch();
  const userConfirm = useConfirm(
    "Changes are not saved, are you sure you want to leave Settings - Psr Camera?"
  );

  const [
    isPsrCaptureProfileConfigUpdated,
    setIsPsrCaptureProfileConfigUpdated,
  ] = useState(false);

  const [psrCaptureProfiles, setPsrCaptureProfiles] = useState(
    [] as NamedPsrCaptureProfile[]
  );
  const [psrCaptureProfile, setPsrCaptureProfile] = useState(
    {} as NamedPsrCaptureProfile
  );
  const [psrCameraCapabillities, setPsrCameraCapabillities] = useState(
    {} as PsrCameraCapabilities
  );
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const fileSelector = useRef<HTMLInputElement>(null);

  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrModule = psrModules[0];

  if (psrModule === undefined) {
    throw new Error("Psr Module is undefined");
  }

  const psrIdentifier = psrModule.identifier;
  const psrCapability = useTypedSelector((state) =>
    getModuleCapability(state, psrIdentifier)
  ).psr;

  if (psrCapability === undefined) {
    throw new Error("Psr Capability is undefined");
  }

  if (psrCapability.psrCameras === undefined) {
    throw new Error("Psr Capability is undefined");
  }

  if (psrCapability.carrierAutoCalibration === undefined) {
    throw new Error("Psr Capability is undefined");
  }


  // need change this logic when multi Psr Camera implemented
  const carrierAutoCalibrationCapability = psrCapability.carrierAutoCalibration;
  const carrierBarcodeScannerCapability = psrCapability.barcodeScanner;
  let psrCameraId = carrierAutoCalibrationCapability.psrCameraId;
  if (
    carrierAutoCalibrationCapability.carrierAutoCalibrationType ===
    ECarrierAutoCalibrationType.PsrCamera
  ) {
    psrCameraId = carrierAutoCalibrationCapability.psrCameraId;
  }
  if (
    carrierBarcodeScannerCapability.barcodeScannerType ===
    EBarcodeScannerType.PsrCamera
  ) {
    psrCameraId = carrierBarcodeScannerCapability.psrCameraId;
  }

  const moduleState = useTypedSelector((state) =>
    getModuleState(state, psrIdentifier)
  );

  const psrState = moduleState.psr;

  if (psrState === undefined) {
    throw new Error("Psr state is null");
  }

  const psrCameraState = psrState.psrCameras.find(
    (x) => x.psrCameraId === psrCameraId
  );

  const moreActionOptions = [
    {
      value: "IMPORT",
      display: "Import",
      icon: <IconImport />,
      disabled: false,
    },
    {
      value: "EXPORT",
      display: "Export",
      icon: <IconExport />,
      disabled: false,
    },
  ];

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const reader = new FileReader();
    reader.onload = async (e) => {
      if (e.target != null) {
        const text = e.target.result;
        if (text === "" || typeof text !== "string") {
          dispatch(
            sendNotification(
              ENotificationType.Error,
              "File is empty or corrupted."
            )
          );
        } else {
          PsrCaptureProfileApi.importPsrCaptureProfile(
            psrCaptureProfile.id,
            text
          ).then((result) => {
            setPsrCaptureProfile(result);
            setIsPsrCaptureProfileConfigUpdated(true);
          });
        }
      }
    };
    if (e.target?.files != null && e.target?.files.length > 0) {
      reader.readAsText(e.target.files[0]);
      if (fileSelector.current) {
        fileSelector.current.value = "";
      }
    }
  };

  const moreActionsClick = (id: string) => {
    switch (id) {
      case "IMPORT":
        fileSelector?.current?.click();
        break;
      case "EXPORT":
        PsrCaptureProfileApi.exportPsrCaptureProfile(psrCaptureProfile);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!isPsrCaptureProfileConfigUpdated) {
      PsrCaptureProfileApi.getPsrCaptureProfiles().then((results) => {
        setPsrCaptureProfiles(results);
      });
    }
  }, [isPsrCaptureProfileConfigUpdated]);

  useEffect(() => {
    const selectedPsrCaptureProfile = psrCaptureProfiles.find(
      (psrCp) => psrCp.id === psrCaptureProfile.id
    );
    if (selectedPsrCaptureProfile) {
      setPsrCaptureProfile(selectedPsrCaptureProfile);
    } else {
      if (psrCaptureProfiles.length > 0) {
        setPsrCaptureProfile(psrCaptureProfiles[0]);
      }
    }
  }, [psrCaptureProfiles]);

  useEffect(() => {
    const psrCamCapabilities = psrCapability?.psrCameras?.find(
      (psrCap) => psrCap.psrCameraId === psrCameraId
    );
    if (psrCamCapabilities) {
      setPsrCameraCapabillities(psrCamCapabilities);
    }
  }, [psrCapability, psrCameraId]);

  useEffect(() => {
    const preventReload = (e: BeforeUnloadEvent) => {
      if (isPsrCaptureProfileConfigUpdated) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = "";
      } else {
        delete e["returnValue"];
      }
    };

    window.addEventListener("beforeunload", preventReload);

    return () => {
      window.removeEventListener("beforeunload", preventReload);
    };
  }, [isPsrCaptureProfileConfigUpdated]);

  useEffect(() => {
    const isStreaming = psrCameraState?.isStreaming ?? false;
    const onLeave = () => {
      if (isStreaming) {
        PsrCameraApi.SetStreaming(psrIdentifier, psrCameraId, false);
      }
      PsrCameraApi.setCaptureProfile(
        psrModule.identifier,
        psrCameraId,
        {
          brightness: 0,
        }
      )
    };
    userConfirm.setOnOk(onLeave);
    userConfirm.setOnCancel(() => {});
    userConfirm.setShowOnLeave(isPsrCaptureProfileConfigUpdated || isStreaming);

    if (isStreaming && isPsrCaptureProfileConfigUpdated) {
      userConfirm.setMessage(
        "Are you sure to leave this page? if you leave, Changes are not saved and Live View will be turned off"
      );
    } else if (isStreaming) {
      userConfirm.setMessage(
        "Are you sure to leave this page? if you leave, Live View will be turned off"
      );
    } else {
      userConfirm.setMessage(
        "Changes are not saved, are you sure you want to leave Settings - Psr Camera?"
      );
    }
  }, [isPsrCaptureProfileConfigUpdated, psrCameraState?.isStreaming]);

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "Psr Camera",
                to: "/settings/psr-camera",
                disabled: false,
              },
            ]}
          />
          <Header.RightSide>
            <HeaderButton
              id={"PSR-CAM-LIVE"}
              label={"Live Stream"}
              content={
                <SwitchButton
                  isOn={psrCameraState?.isStreaming ?? false}
                  withLetter={true}
                  onToggle={() => {
                    PsrCameraApi.SetStreaming(
                      psrIdentifier,
                      psrCameraId,
                      !(psrCameraState?.isStreaming ?? false)
                    );
                  }}
                />
              }
              onClick={() => {
                PsrCameraApi.SetStreaming(
                  psrIdentifier,
                  psrCameraId,
                  !(psrCameraState?.isStreaming ?? false)
                );
              }}
              disabled={false}
            />
            <HeaderDropDownButton
              id={"MORE_ACTIONS"}
              label={"More Actions"}
              icon={<IconMoreAction />}
              onChange={moreActionsClick}
              iconDisplayValues={moreActionOptions}
            />
            <MtiButton
              size="large"
              variant="secondary"
              style={{
                margin: "auto 10px auto 25px",
              }}
              disabled={!isPsrCaptureProfileConfigUpdated}
              onClick={() => {
                setIsPsrCaptureProfileConfigUpdated(false);
              }}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              size="large"
              disabled={!isPsrCaptureProfileConfigUpdated}
              style={{
                margin: "auto 16px auto 10px",
              }}
              onClick={() => {
                setShowConfirmSave(true);
              }}
            >
              APPLY
            </MtiButton>
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <SettingsNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.Content>
        <PsrCameraLiveImage
          psrCameraId={psrCameraId}
          psrIdentifier={psrIdentifier}
        />
      </LayoutContent.Content>
      <LayoutContent.CommandPanel>
        <RaspiCameraSettings
          psrCameraId={psrCameraId}
          psrIdentifier={psrIdentifier}
          psrCameraCapabilities={psrCameraCapabillities}
          isPsrCaptureProfileConfigUpdated={isPsrCaptureProfileConfigUpdated}
          setIsPsrCaptureProfileConfigUpdated={
            setIsPsrCaptureProfileConfigUpdated
          }
          psrCaptureProfile={psrCaptureProfile}
          psrCaptureProfiles={psrCaptureProfiles}
          setPsrCaptureProfile={setPsrCaptureProfile}
        />
        <input
          ref={fileSelector}
          type="file"
          onChange={(e) => uploadFile(e)}
          style={{ display: "none" }}
          accept=".xml"
        />
        <Modal
          show={showConfirmSave}
          onHide={() => setShowConfirmSave(false)}
          style={{
            width: 686,
            minHeight: 422,
          }}
        >
          <Modal.Body>
            <UserConfirmationModalView
              title={`Configure ${psrCaptureProfile.captureProfileName}`}
              message={`Save ${psrCaptureProfile.captureProfileName} configuration`}
              subMessage={"Do you want to proceed?"}
              variant="warning"
            />
          </Modal.Body>
          <Modal.Footer
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              columnGap: 12,
            }}
          >
            <MtiButton
              variant="secondary"
              size="large"
              style={{
                width: 157,
              }}
              onClick={() => {
                setShowConfirmSave(false);
              }}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              variant="primary"
              size="large"
              style={{
                width: 157,
              }}
              onClick={() => {
                PsrCaptureProfileApi.editPsrCaptureProfile(
                  psrCaptureProfile
                ).then((result) => {
                  setIsPsrCaptureProfileConfigUpdated(false);
                  setShowConfirmSave(false);
                  dispatch(
                    sendNotification(
                      ENotificationType.Success,
                      "The changes has been saved successfully"
                    )
                  );
                });
              }}
            >
              OK
            </MtiButton>
          </Modal.Footer>
        </Modal>
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
}
